import { useMutation, useQuery } from "@apollo/client";
import { SOCIAL_PLATFORMS, type SocialPlatformKey } from "@salon/types";
import { isEqual } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { SalonSocialLink } from "@/gql/graphql";
import { GET_SALON_SOCIAL_LINKS, UPDATE_SALON_SOCIAL_LINKS } from "@/graphql/salon";
import { useSalonStore } from "@/lib/store/salon";
import { RESET_OPTIONS } from "./reset-options";

export type SocialLinksFormData = Record<SocialPlatformKey, string>;

function toFormData(links: Pick<SalonSocialLink, "platform" | "url">[]): SocialLinksFormData {
  const values = Object.fromEntries(
    SOCIAL_PLATFORMS.map((platform) => [platform, ""])
  ) as SocialLinksFormData;
  for (const link of links) {
    values[link.platform] = link.url;
  }
  return values;
}

/**
 * Reads/replaces the salon's social links. The backend normalizes handles and
 * URLs on write, so the form is re-seeded from the server response to show the
 * stored value.
 */
export function useSocialLinks() {
  const { t } = useTranslation("settings");
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id ?? "";
  const [saving, setSaving] = useState(false);

  const { data, loading } = useQuery(GET_SALON_SOCIAL_LINKS, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-and-network",
  });

  const [updateSocialLinks] = useMutation(UPDATE_SALON_SOCIAL_LINKS);

  const form = useForm<SocialLinksFormData>({ defaultValues: toFormData([]) });

  // Seed once per salon: later refetches must not overwrite what the owner is
  // typing, but switching salons (no remount) must load the new salon's links.
  const seededSalonId = useRef<string | null>(null);
  // Last links known to be stored on the server.
  const savedLinks = useRef(toFormData([]));
  const salon = data?.getSalon;
  useEffect(() => {
    if (salon && salon.id === salonId && seededSalonId.current !== salonId) {
      savedLinks.current = toFormData(salon.socialLinks);
      form.reset(savedLinks.current, RESET_OPTIONS);
      seededSalonId.current = salonId;
    }
  }, [salon, salonId, form]);

  const save = useCallback(async () => {
    const values = form.getValues();
    // Diff against the server links instead of reading `form.formState.isDirty`,
    // which React Compiler can memoize away inside this callback.
    if (!salonId || isEqual(values, savedLinks.current)) return;

    setSaving(true);
    try {
      const result = await updateSocialLinks({
        variables: {
          salonId,
          input: {
            links: SOCIAL_PLATFORMS.map((platform) => ({ platform, url: values[platform] })),
          },
        },
        // The mutation returns bare links that Apollo can't attach to the salon, so
        // write them back; otherwise reopening the page seeds the pre-save links.
        update(cache, { data: updated }) {
          if (!updated) return;
          cache.writeQuery({
            query: GET_SALON_SOCIAL_LINKS,
            variables: { salonId },
            data: {
              getSalon: {
                __typename: "Salon",
                id: salonId,
                socialLinks: updated.updateSalonSocialLinks,
              },
            },
          });
        },
      });
      savedLinks.current = toFormData(result.data?.updateSalonSocialLinks ?? []);
      form.reset(savedLinks.current, RESET_OPTIONS);
      toast.success(t("storefront.socialLinks.saved"));
    } catch (err) {
      // Keep the owner's input so they can fix the rejected link.
      toast.error(err instanceof Error ? err.message : t("storefront.socialLinks.saveFailed"));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [form, salonId, t, updateSocialLinks]);

  const discard = useCallback(() => {
    form.reset(undefined, RESET_OPTIONS);
  }, [form]);

  // `cache-and-network` keeps `loading` true during background refetches even
  // when data is already shown; only block input until the form is seeded.
  const initialLoading = loading && salon?.id !== salonId;

  return { form, loading: initialLoading, saving, save, discard };
}
